import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ABOUT_CONTENT, IMAGES, SOCIAL_LINKS } from '../../../constants';
import { Button, Card, FeatureItem, Icon, StatCard } from '../../common';

// Instagram SVG Icon
const InstagramIcon = () => (
  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

/**
 * Highlighted Text Component
 * Renders text with gold highlight styling
 */
const HighlightedBio = ({ segments }) => {
  return (
    <p className="text-gray-300 leading-relaxed text-sm">
      {segments.map((segment, index) => (
        segment.highlight ? (
          <span 
            key={index} 
            className="text-gold font-semibold"
          >
            {segment.text}
          </span>
        ) : (
          <span key={index}>{segment.text}</span>
        )
      ))}
    </p>
  );
};

/**
 * About Section - Biography, stats, and features
 * Showcases the artist's background and capabilities
 */
const AboutSection = () => {
  const navigate = useNavigate();

  return (
    <section id="about" className="relative bg-background-dark px-6 py-20 space-y-12">
      {/* Section Header */}
      <div className="space-y-4 text-center">
        <span className="inline-flex items-center justify-center p-3 rounded-full bg-primary/10 text-primary mb-2">
          <Icon name="artist" size="lg" />
        </span>
        <h2 className="text-3xl font-bold leading-tight">
          {ABOUT_CONTENT.title}
        </h2>
        <p className="text-sm text-gray-400 max-w-xs mx-auto">
          {ABOUT_CONTENT.subtitle}
        </p>
      </div>

      {/* Portrait with Stats */}
      <div className="relative group">
        {/* Glow Effect */}
        <div className="absolute -inset-0.5 bg-gradient-to-b from-primary/50 to-transparent rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-1000" />
        
        <div className="relative aspect-[3/4] w-full rounded-2xl overflow-hidden bg-gray-800 shadow-2xl">
          {/* Image */}
          <div 
            className="w-full h-full bg-cover bg-center transform transition-transform duration-700 group-hover:scale-105"
            style={{ backgroundImage: `url('${IMAGES.aboutPortrait}')` }}
            role="img"
            aria-label="Vertical portrait of Matisa smiling holding her guitar in a studio setting"
          />
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
          
          {/* Stats Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <Card variant="glass" padding="md" hover={false}>
              <div className="flex justify-between items-center">
                {ABOUT_CONTENT.stats.map((stat, index) => (
                  <React.Fragment key={stat.label}>
                    <StatCard value={stat.value} label={stat.label} />
                    {index < ABOUT_CONTENT.stats.length - 1 && (
                      <div className="h-10 w-px bg-white/20" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Bio and Features */}
      <div className="space-y-6">
        {/* Main Bio with Highlights */}
        <HighlightedBio segments={ABOUT_CONTENT.bioHighlights} />
        
        {/* Extended Bio */}
        {ABOUT_CONTENT.bioExtended && (
          <p className="text-gray-400 leading-relaxed text-sm">
            {ABOUT_CONTENT.bioExtended}
          </p>
        )}

        {/* Availability */}
        {ABOUT_CONTENT.availability && (
          <p className="text-gray-500 leading-relaxed text-xs italic border-l-2 border-gold/50 pl-3">
            {ABOUT_CONTENT.availability}
          </p>
        )}
        
        {/* Feature Items */}
        <div className="grid grid-cols-1 gap-4 pt-4">
          {ABOUT_CONTENT.features.map((feature) => (
            <FeatureItem
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
        
        {/* CTA Buttons */}
        <div className="space-y-3 pt-2">
          {/* Shows Page Link */}
          <Button
            variant="outline"
            size="lg"
            icon="music_note"
            fullWidth
            onClick={() => {
              const scrollContainer = document.querySelector('.overflow-y-auto');
              if (scrollContainer) scrollContainer.scrollTo(0, 0);
              navigate('/shows');
            }}
          >
            See What I Do
          </Button>

          {/* Instagram CTA */}
          <Button
            variant="white"
            size="lg"
            icon="arrow_outward"
            fullWidth
            onClick={() => window.open(SOCIAL_LINKS.instagram, '_blank')}
          >
            <span className="flex items-center gap-2">
              <InstagramIcon />
              <span>Follow on Instagram</span>
            </span>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
